import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ShortCuts } from '@cube3/ui/src/keyboard-shortcuts/prefab/ShortCuts';
import Vertical from '@cube3/ui/src/Layout/vertical';
import { matchPath, useHistory, useLocation } from 'react-router';
import {
  urlStructureAccountSettings,
  urlStructureAdminCenter,
  urlStructureLibraryAsset
} from '../../routing/routingPaths';
import { useDispatch } from 'react-redux';
import { setTour } from '@cube3/state/src/redux/ducks/ui/actions';
import { useOpenDocs } from './hooks/useOpenDocs';
import { ContextMenu } from '@cube3/cubicle/src/core/molecules/ContextMenu';
import useOutsideClick from '@cube3/cubicle/src/helpers/hooks/UseOutsideClick';
import { ContextMenuListItem } from '@cube3/cubicle/src/core/molecules/ContextMenu/ContextMenuListItem';
import { useMenuToggle } from '@cube3/cubicle/src/helpers/hooks/useMenuToggle';
import { getEnvVar } from '../../../../utils/getEnvVar';
import { useModalActions } from '../Modals/modalActions';
import { uploadWorkerSettings } from '@cube3/state/src/wrapped-cube-client/uploader/extendedupload';
import { MenuItem } from '@cube3/cubicle/src/core/atoms/MenuItem';
import { isMobile } from 'react-device-detect';
import { MobileSlideSurface } from '@cube3/cubicle/src/components/Surface/MobileSlideSurface';


export const HelpIndicator = () => {

  const { toggle, close, setAnchor, isMenuOpen, anchorEl } = useMenuToggle();

  const [show, setShow] = useState(false);

  const {
    handleOpenDocs: handleOpenDocsReal,
    hasDocs,
    docsAuthError
  } = useOpenDocs({
    isMenuOpen,
    toggle
  });

  const handleOpenDocs = useCallback(() => {
    handleOpenDocsReal();
  }, [handleOpenDocsReal]);

  const handleShowShortcuts = useCallback(() => {
    setShow((current) => !current);
    if (isMenuOpen) {
      toggle();
    }
  }, [setShow, toggle, isMenuOpen]);

  const dispatch = useDispatch();
  const handleOpenTour = useCallback(() => {
    dispatch(setTour(true));
    toggle();
  }, [dispatch, setTour, toggle]);
  // disable tours in admin-center, detail view and account settings page
  const { pathname } = useLocation();
  const inAdminCenterView = matchPath(pathname, urlStructureAdminCenter);
  const inAccountSettingsView = matchPath(
    pathname,
    urlStructureAccountSettings
  );
  const inDetailsView = matchPath(pathname, urlStructureLibraryAsset);
  const disableTour =
    !!inAccountSettingsView || !!inAdminCenterView || !!inDetailsView;

  const rootRef = useOutsideClick(close);

  const history = useHistory();
  const location = useLocation<{ __from: string }>();
  const onClickItem = useCallback(
    (url) => () => {
      if (!url) return;
      if (url.match(/^http[s]?:\/\//)) {
        window.open(url, '_blank', 'noopener noreferrer');
      } else {
        history.push({
          pathname: url,
          state: {
            __from:
              (location.state && location.state.__from) || location.pathname
          }
        });
      }
    },
    [location, history]
  );

  const { openModal, closeAllModals } = useModalActions();

  const handleWorkerToggle = useCallback(() => {
    uploadWorkerSettings.toggleWorker();
  }, []);

  const [useUploadWorker, setUseUploadWorker] = useState(
    !!uploadWorkerSettings.workerStack
  );

  useEffect(() => {
    uploadWorkerSettings.onchange = setUseUploadWorker;
  }, []);

  const items = useMemo(() => {

    const contactUsUrl = getEnvVar('CONTACT_US_URL');

    return [
      hasDocs && {
        name: 'User guide',
        onClick: handleOpenDocs,
        disabled: !!docsAuthError,
        leftIcon: 'guide' as const,
        showChevron: false
      },
      {
        name: 'Keyboard shortcuts',
        onClick: handleShowShortcuts,
        leftIcon: 'keyboard' as const,
        showChevron: false
      },
      !disableTour && {
        name: 'Restart tour',
        onClick: handleOpenTour,
        leftIcon: 'refresh' as const,
        showChevron: false
      },
      {
        name: 'Contact us',
        onClick: onClickItem(contactUsUrl),
        leftIcon: 'email' as const,
        disabled: !contactUsUrl,
        showChevron: false
      },
      {
        name: 'Terms and Policies',
        onClick: onClickItem('/legal/policies/'),
        leftIcon: 'contract' as const,
        showChevron: false
      },
      {
        name: 'Cookie settings',
        leftIcon: 'cookie' as const,
        onClick: (ev) => {
          isMobile ? ev.stopPropagation() : '';
          openModal('cookie_settings');
        },
        showChevron: false
      },
      {
        name: `Legacy Uploads [${!useUploadWorker ? 'enabled' : 'disabled'}]`,
        leftIcon: 'upload' as const,
        onClick: (ev) => {
          ev.stopPropagation();
          handleWorkerToggle();
        },
        showChevron: false
      }
    ].filter(Boolean);
  }, [
    hasDocs,
    disableTour,
    docsAuthError,
    handleOpenDocs,
    handleShowShortcuts,
    handleOpenTour,
    openModal,
    onClickItem,
    handleWorkerToggle,
    useUploadWorker
  ]);

  return (
    <div ref={rootRef}>
      <MenuItem
        value="Help"
        itemIcon="help"
        onClick={toggle}
        extraButtonProperties={{ buttonRef: setAnchor }}
        selected={isMenuOpen}
      />

      {isMenuOpen && (
        <>
          {isMobile ? (
            <MobileSlideSurface
              onClose={() => closeAllModals()}
              closeOnClick={true}
            >
              <Vertical>
                {items.map((item) => (
                  <ContextMenuListItem key={item.name} {...item} />
                ))}
              </Vertical>
            </MobileSlideSurface>
          ) : (
            <ContextMenu
              anchor={anchorEl}
              placement="right-start"
              scrollbarHeight={300}
              popupGap={-7}
            >
              <Vertical>
                {items.map((item) => (
                  <ContextMenuListItem key={item.name} {...item} />
                ))}
              </Vertical>
            </ContextMenu>
          )}
        </>
      )}
      <ShortCuts open={show} close={handleShowShortcuts} />
    </div>
  );
};
